import { sb } from '../supabase';
import { MIN_SAMPLE } from './growthRange';
import { latestSessions } from './claritySessions';
import { AI_SOURCE, sourceName } from './growthLabels';

// «أهم الملاحظات» — owner, 2026-11-01: 3–5 plain Arabic sentences from the
// events in the selected range. Rules only, no AI. Each sentence carries the
// sessions behind it (so it opens their recordings) and the number it rests
// on; below MIN_SAMPLE it is marked «بيانات قليلة», never stated as a trend.

export const FINDING_EVENTS = [
  'session_started', 'screen_viewed', 'page_hidden', 'product_viewed', 'cart_item_added', 'cart_viewed', 'cart_minimum_blocked',
  'checkout_started', 'payment_started', 'payment_succeeded', 'auth_viewed', 'login_completed', 'signup_completed', 'search_results_shown',
];
const MAX = 5;

// Arabic counting for people, times and searches (1, 2, 3–10, 11+).
export function people(n) {
  if (n === 1) return 'شخص واحد';
  if (n === 2) return 'شخصان';
  return n <= 10 ? `${n} أشخاص` : `${n} شخصاً`;
}
const verb = (n, one, two, many) => (n === 1 ? one : n === 2 ? two : many);
export function times(n) {
  if (n === 1) return 'مرة واحدة';
  if (n === 2) return 'مرتين';
  return n <= 10 ? `${n} مرات` : `${n} مرة`;
}
const visits = (n) => (n === 1 ? 'زيارة واحدة' : n === 2 ? 'زيارتان' : n <= 10 ? `${n} زيارات` : `${n} زيارة`);
const searches = (n) => (n === 1 ? 'عملية بحث واحدة' : n === 2 ? 'عمليتا بحث' : n <= 10 ? `${n} عمليات بحث` : `${n} عملية بحث`);
const num = (v) => Number(v) || 0;
const fmtNum = (n, d = 0) => Number(n).toLocaleString('en-US', { maximumFractionDigits: d });
const sar = (n) => `${fmtNum(n)} ر.س`;

function bySession(events) {
  const s = {};
  events.forEach((e) => {
    if (!e.session_id) return;
    const x = s[e.session_id] || (s[e.session_id] = { id: e.session_id, ev: {}, rows: [], start: null });
    (x.ev[e.event_name] = x.ev[e.event_name] || []).push(e);
    x.rows.push(e);
    if (e.event_name === 'session_started') x.start = e;
  });
  return Object.values(s);
}
const has = (s, name) => !!s.ev[name];
const sessionsOf = (list) => latestSessions(list.flatMap((s) => s.rows), 8);
const finding = (key, text, list, base) => ({ key, text, sessions: sessionsOf(list), base, low: base < MIN_SAMPLE });

// The rules, in the order they matter to the owner; the first MAX that apply.
const RULES = [
  // Stopped at the cart minimum.
  (S) => {
    const hit = S.filter((s) => has(s, 'cart_minimum_blocked'));
    if (!hit.length) return null;
    const ev = hit.map((s) => s.ev.cart_minimum_blocked.at(-1).props || {});
    const mins = ev.map((p) => num(p.total_sar) + num(p.gap_sar)).filter((m) => m > 0);
    const minimum = mins.length ? mins.sort((a, b) => a - b)[Math.floor(mins.length / 2)] : null;
    const totals = ev.map((p) => num(p.total_sar));
    const avg = totals.reduce((a, b) => a + b, 0) / totals.length;
    const n = hit.length;
    let text = `${people(n)} ${verb(n, 'وصل', 'وصلا', 'وصلوا')} للسلة ${verb(n, 'ووقف', 'ووقفا', 'ووقفوا')} عند الحد الأدنى${minimum ? ` ${sar(minimum)}` : ''}`;
    text += avg > 0 ? (n === 1 ? ` — كانت سلّته ${sar(avg)}` : ` — متوسط سلالهم ${sar(avg)}`) : (n === 1 ? ' — وسلّته فارغة' : ' — وسلالهم فارغة');
    return finding('cart_minimum', text, hit, n);
  },
  // Started checkout, did not go on to pay.
  (S) => {
    const started = S.filter((s) => has(s, 'checkout_started'));
    if (!started.length) return null;
    const paid = started.filter((s) => has(s, 'payment_started'));
    const n = started.length; const m = paid.length;
    const text = m === 0
      ? `${people(n)} ${verb(n, 'بدأ', 'بدآ', 'بدأوا')} الدفع ولم ${verb(n, 'يصل', 'يصلا', 'يصل أحد منهم')} لصفحة الدفع`
      : m === n ? `${people(n)} ${verb(n, 'بدأ', 'بدآ', 'بدأوا')} الدفع ${verb(n, 'ووصل', 'ووصلا', 'ووصلوا كلهم')} لصفحة الدفع`
      : `${people(n)} ${verb(n, 'بدأ', 'بدآ', 'بدأوا')} الدفع، ووصل ${m === 1 ? 'واحد' : fmtNum(m)} منهم لصفحة الدفع`;
    const stopped = started.filter((s) => !has(s, 'payment_started'));
    return finding('checkout', text, stopped.length ? stopped : started, n);
  },
  // Opened sign-in, did not finish.
  (S) => {
    const viewed = S.filter((s) => has(s, 'auth_viewed'));
    if (!viewed.length) return null;
    const done = viewed.filter((s) => has(s, 'login_completed') || has(s, 'signup_completed'));
    const n = viewed.length; const m = done.length;
    const text = m === 0
      ? `${people(n)} ${verb(n, 'فتح', 'فتحا', 'فتحوا')} صفحة الدخول ولم ${verb(n, 'يكمل', 'يكملا', 'يكمل أحد منهم')} الدخول أو التسجيل`
      : m === n ? `${people(n)} ${verb(n, 'فتح', 'فتحا', 'فتحوا')} صفحة الدخول ${verb(n, 'وأكمل', 'وأكملا', 'وأكملوا كلهم')} الدخول أو التسجيل`
      : `${people(n)} ${verb(n, 'فتح', 'فتحا', 'فتحوا')} صفحة الدخول، وأكمل ${m === 1 ? 'واحد' : fmtNum(m)} منهم الدخول أو التسجيل`;
    const left = viewed.filter((s) => !(has(s, 'login_completed') || has(s, 'signup_completed')));
    return finding('auth', text, left.length ? left : viewed, n);
  },
  // Searches that found nothing.
  (S) => {
    const zero = S.flatMap((s) => (s.ev.search_results_shown || []).filter((e) => String(e.props?.zero_results) === 'true').map((e) => ({ s, q: String(e.props?.query || '').trim() })));
    if (!zero.length) return null;
    const byQ = {};
    zero.forEach((z) => { if (z.q) byQ[z.q] = (byQ[z.q] || 0) + 1; });
    const qs = Object.entries(byQ).sort((a, b) => b[1] - a[1]);
    const n = zero.length;
    let text;
    if (qs.length === 1) text = `${searches(n)} عن «${qs[0][0]}» لم ${n === 2 ? 'تجدا' : 'تجد'} شيئاً`;
    else text = `${searches(n)} لم ${n === 2 ? 'تجدا' : 'تجد'} شيئاً${qs.length ? `، أكثرها «${qs[0][0]}» (${times(qs[0][1])})` : ''}`;
    return finding('zero_results', text, [...new Set(zero.map((z) => z.s))], zero.length);
  },
  // Visitors from AI assistants, and how far they browsed.
  (S) => {
    const src = (s) => s.start?.props?.utm_source || s.start?.props?.referrer_domain || '';
    const ai = S.filter((s) => AI_SOURCE.test(src(s)));
    if (!ai.length) return null;
    const names = {};
    ai.forEach((s) => { const k = sourceName(src(s)); names[k] = (names[k] || 0) + 1; });
    const name = Object.entries(names).sort((a, b) => b[1] - a[1])[0][0];
    const pages = ai.map((s) => (s.ev.screen_viewed || []).length + (s.ev.product_viewed || []).length).filter((p) => p > 0);
    const avg = pages.length ? pages.reduce((a, b) => a + b, 0) / pages.length : null;
    const n = ai.length;
    const who = n === 1 ? 'زائر واحد' : n === 2 ? 'زائران' : n <= 10 ? `${n} زوار` : `${n} زائراً`;
    let text = `${who} ${verb(n, 'جاء', 'جاءا', 'جاءوا')} من ${Object.keys(names).length === 1 ? name : `منصات الذكاء الاصطناعي (أكثرها ${name})`}`;
    if (avg) text += n === 1 ? ` وتصفّح ${fmtNum(avg, 1)} صفحات` : ` وتصفّح كل واحد ${fmtNum(avg, 1)} صفحات في المتوسط`;
    return finding('ai_source', text, ai, n);
  },
  // Looked at products, added nothing.
  (S) => {
    const viewed = S.filter((s) => has(s, 'product_viewed'));
    if (!viewed.length) return null;
    const added = viewed.filter((s) => has(s, 'cart_item_added'));
    const n = viewed.length; const m = added.length;
    const saw = `${people(n)} ${verb(n, 'شاهد', 'شاهدا', 'شاهدوا')} منتجات`;
    const text = m === 0 ? `${saw} ولم ${verb(n, 'يضف', 'يضيفا', 'يضف أحد منهم')} شيئاً للسلة`
      : m === n ? `${saw} ${verb(n, 'وأضاف', 'وأضافا', 'وأضافوا كلهم')} للسلة`
      : `${saw}، وأضاف ${m === 1 ? 'واحد' : fmtNum(m)} منهم للسلة`;
    const none = viewed.filter((s) => !has(s, 'cart_item_added'));
    return finding('product_cart', text, none.length ? none : viewed, n);
  },
  // Left within 10 seconds on a single page.
  (S) => {
    const quick = S.filter((s) => (s.ev.screen_viewed || []).length <= 1 && (s.ev.page_hidden || []).some((e) => num(e.props?.seconds_on_screen) > 0 && num(e.props?.seconds_on_screen) < 10));
    if (!quick.length) return null;
    const n = quick.length;
    return finding('quick_exit', `${visits(n)} ${n === 2 ? 'انتهتا' : 'انتهت'} خلال أقل من 10 ثوانٍ على صفحة واحدة`, quick, n);
  },
];

export function findings(events) {
  const S = bySession(events || []);
  const out = [];
  for (const rule of RULES) {
    if (out.length >= MAX) break;
    const f = rule(S);
    if (f) out.push(f);
  }
  return { list: out, sessions: S.length };
}

export const ROW_CAP = 20000;
export async function fetchFindings(win) {
  const { data, error } = await sb.from('growth_events_clean').select('event_name, occurred_at, session_id, props')
    .in('event_name', FINDING_EVENTS).gte('occurred_at', new Date(win.start).toISOString()).lt('occurred_at', new Date(win.end).toISOString())
    .order('occurred_at', { ascending: true }).limit(ROW_CAP);
  if (error) throw error;
  const rows = data || [];
  return { ...findings(rows), capped: rows.length >= ROW_CAP };
}
